import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { GraphQLTimesheetService } from '../../services/graphqlTimesheet';
import { GraphQLPMSService } from '../../services/graphqlPms';
import { canCreateNomination, canReviewNomination, canManageAllNominations } from '../../utils/nominationPermissions';
import { useNominationRole } from '../../hooks/useNominationRole';
import { canViewTeamExpenses, canViewAllExpenses } from '../../utils/rolePermissions';
import { Expense, ExpenseStatus } from '../../types/expense';
import { Nomination, NominationStatus } from '../../types/nomination';
import { WeeklyTimesheet, TimesheetStatus } from '../../types/timesheet';
import { Objective, ObjectiveStatus } from '../../types/pms';
import { UserRole } from '../../types/user';
import { useFavorites } from '../../hooks/useFavorites';
import { getFavoritablePages, resolveFavorites, groupPages, MAX_FAVORITES } from '../../utils/favoritePages';
import { getUpcomingHolidays, daysUntilHoliday } from '../../data/holidays';
import { useWebsiteImages } from '../../hooks/useWebsiteImages';
import { ImagePlacement } from '../../types/websiteImage';
import './HomePage.css';

/*
 * Lazy, with its own Suspense boundary further down.
 *
 * The card pulls in the attendance orchestrator, the engine and the shift/leave services. Home
 * is the first screen every user sees after login, and none of that is needed to paint it — an
 * eager import puts the whole attendance module in the initial bundle for the sake of one card.
 */
const AttendanceSummaryWidget = React.lazy(() => import('../attendance/AttendanceSummaryWidget'));

/*
 * The carousel is configured, not compiled.
 *
 * This was an array of twenty-one `{ src, caption }` literals, so adding a photograph from the
 * last office party meant editing this file, getting it reviewed, building and deploying — which
 * is why the gallery aged. It is now whatever a Super Admin has put in Settings → Image
 * Management, and those twenty-one are seeded there so nothing changed on the day it moved.
 *
 * useWebsiteImages hands back only the ACTIVE images, in the configured order, each with a URL
 * that resolves — and an empty list rather than an error if the API is unreachable or nothing has
 * been configured. Everything below therefore has to cope with there being no images at all,
 * which the hardcoded version never had to.
 */

/**
 * Chip icon per favouritable section — keys are FavoritablePage.group.
 *
 * Keyed on group rather than path so a new page inside an existing section inherits the
 * right icon with no edit here; only a brand-new section needs a line adding, and the
 * fallback keeps an unmapped one looking deliberate rather than blank.
 */
const FAVORITE_GROUP_ICONS: Record<string, string> = {
  Expenses: '💳',
  Leave: '🌴',
  Timesheets: '🕒',
  'Shift Roster': '📅',
  'Award Nominations': '🏆',
  Performance: '🎯',
  Administration: '⚙️',
};

const motivationalQuotes = [
  { text: "Coming together is a beginning, staying together is progress, and working together is success.", author: "CEO, Creyente InfoTech" },
  { text: "Alone we can do so little; together we can do so much.", author: "CEO, Creyente InfoTech" },
  { text: "Talent wins games, but teamwork and intelligence win championships.", author: "CEO, Creyente InfoTech" },
  { text: "Great things in business are never done by one person. They're done by a team of people.", author: "CEO, Creyente InfoTech" },
  { text: "The strength of the team is each individual member. The strength of each member is the team.", author: "CEO, Creyente InfoTech" },
];

const HOME_CACHE_TTL_MS = 5 * 60 * 1000;

const readHomeCache = <T,>(key: string): T | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    if (!parsed?.createdAt || Date.now() - parsed.createdAt > HOME_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value as T;
  } catch {
    return null;
  }
};

const writeHomeCache = <T,>(key: string, value: T) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), value }));
  } catch {
    // Browser storage can be unavailable or full; live data still renders normally.
  }
};

const runWhenIdle = (callback: () => void, timeout = 800) => {
  const scheduler = (window as any).requestIdleCallback;
  if (typeof scheduler === 'function') {
    const id = scheduler(callback, { timeout });
    return () => (window as any).cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(callback, timeout);
  return () => window.clearTimeout(id);
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [loadingNominations, setLoadingNominations] = useState(true);
  const [animatedStats, setAnimatedStats] = useState(false);
  const [editingFavorites, setEditingFavorites] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  /*
   * The carousel, as configured in Settings → Image Management.
   *
   * Mapped to the { src, caption } shape the markup below already used, so replacing the
   * hardcoded array touched the data and not the rendering. `name` is the caption: it is what a
   * Super Admin types when they add the image, and what the old array called `caption`.
   */
  const { images: configuredImages } = useWebsiteImages(ImagePlacement.HOME_CAROUSEL);
  const carouselImages = useMemo(
    () => configuredImages.map(image => ({ src: image.url, caption: image.name })),
    [configuredImages]
  );

  const { nominationRole } = useNominationRole(user);
  const { favorites, isFavorite, toggleFavorite, removeFavorite, isFull } = useFavorites(user);
  const todayQuote = motivationalQuotes[new Date().getDay() % motivationalQuotes.length];
  const homeCachePrefix = user ? `home:v2:${user.email || user.id}:${user.role}` : null;

  // Animate stats on scroll into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimatedStats(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  /*
   * Auto-advance.
   *
   * `slideCount || 1` on every wrap, because every one of these is a modulo and `x % 0` is NaN —
   * which would set currentSlide to NaN, translate the track by NaN and blank the hero. The
   * hardcoded array could never be empty; a configured list can be, on the first load, in an
   * environment where the module is not deployed, or simply because nobody has added an image.
   *
   * The timer is skipped entirely when there is nothing to advance through, rather than ticking
   * four times a minute over an empty list for as long as the tab is open.
   */
  const slideCount = carouselImages.length;

  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (slideCount < 2) return;
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slideCount);
    }, 4000);
  }, [slideCount]);

  useEffect(() => {
    startAutoPlay();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [startAutoPlay]);

  /*
   * Keep the index inside the list when the list changes underneath it.
   *
   * The images arrive after the first paint, and a Super Admin removing one while somebody has
   * Home open shortens it. Either way an index that was valid a moment ago can now be past the
   * end, and the caption reads that index directly.
   */
  useEffect(() => {
    setCurrentSlide(prev => (slideCount === 0 ? 0 : Math.min(prev, slideCount - 1)));
  }, [slideCount]);

  const goToSlide = (index: number) => { setCurrentSlide(index); startAutoPlay(); };
  const prevSlide = () => { setCurrentSlide(prev => (prev - 1 + slideCount) % (slideCount || 1)); startAutoPlay(); };
  const nextSlide = () => { setCurrentSlide(prev => (prev + 1) % (slideCount || 1)); startAutoPlay(); };

  // Fetch expense data
  useEffect(() => {
    if (!user) return;
    const cacheKey = `${homeCachePrefix}:expenses`;
    const cached = readHomeCache<Expense[]>(cacheKey);
    if (cached) {
      setExpenses(cached);
      setLoadingExpenses(false);
    }
    let cancelled = false;
    const fetchExpenses = async () => {
      try {
        if (!cached) setLoadingExpenses(true);
        let result;
        if (canViewAllExpenses(user.role)) {
          result = await ExpenseServiceWrapper.getAllExpenses(user.role);
        } else if (canViewTeamExpenses(user.role)) {
          result = await ExpenseServiceWrapper.getTeamExpenses(user.id, user.email);
        } else {
          result = await ExpenseServiceWrapper.getMyExpenses(user.id, user.email);
        }
        if (cancelled) return;
        const items = result.items || [];
        setExpenses(items);
        writeHomeCache(cacheKey, items);
      } catch (err) {
        console.error('Failed to fetch expenses:', err);
      } finally {
        if (!cancelled) setLoadingExpenses(false);
      }
    };
    fetchExpenses();
    return () => { cancelled = true; };
  }, [user, homeCachePrefix]);

  // Fetch nomination data based on role
  useEffect(() => {
    if (!user) return;
    const cacheKey = `${homeCachePrefix}:nominations:${nominationRole}`;
    const cached = readHomeCache<Nomination[]>(cacheKey);
    if (cached) {
      setNominations(cached);
      setLoadingNominations(false);
    }
    let cancelled = false;
    const fetchNominations = async () => {
      try {
        if (!cached) setLoadingNominations(true);
        let data: Nomination[] = [];
        if (canManageAllNominations(nominationRole) || canReviewNomination(nominationRole)) {
          data = await NominationServiceWrapper.getAllNominations();
        } else if (canCreateNomination(nominationRole)) {
          data = await NominationServiceWrapper.getMyNominations(user.id, user.email);
        } else {
          const feed = await NominationServiceWrapper.getFeed(1);
          data = feed.items || [];
        }
        if (cancelled) return;
        setNominations(data);
        writeHomeCache(cacheKey, data);
      } catch (err) {
        console.error('Failed to fetch nominations:', err);
      } finally {
        if (!cancelled) setLoadingNominations(false);
      }
    };
    const cancelIdle = runWhenIdle(fetchNominations, cached ? 1200 : 300);
    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [user, nominationRole, homeCachePrefix]);

  // Fetch timesheet and PMS summaries for the role-aware home overview.
  useEffect(() => {
    if (!user) return;
    const cacheKey = `${homeCachePrefix}:operational`;
    const cached = readHomeCache<{ timesheets: WeeklyTimesheet[]; objectives: Objective[] }>(cacheKey);
    if (cached) {
      setTimesheets(cached.timesheets || []);
      setObjectives(cached.objectives || []);
    }
    let cancelled = false;

    const fetchOperationalSummaries = async () => {
      try {
        const isAdminView = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.HR_ADMIN || user.role === UserRole.FINANCE_ADMIN;
        const nextTimesheets = isAdminView
          ? (await Promise.all([
              TimesheetStatus.SUBMITTED,
              TimesheetStatus.APPROVED,
              TimesheetStatus.RETURNED,
            ].map(status => GraphQLTimesheetService.getTimesheetsByStatus(status)))).flat()
          : await GraphQLTimesheetService.getMyTimesheets(user.email);

        let objectiveData: Objective[] = [];
        if (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.HR_ADMIN) {
          objectiveData = await GraphQLPMSService.getAllObjectives();
        } else if (user.role === UserRole.MANAGER) {
          objectiveData = await GraphQLPMSService.getObjectivesByManager(user.email);
        } else if (!isAdminView) {
          objectiveData = await GraphQLPMSService.getEmployeeObjectives(user.email);
        }
        if (cancelled) return;
        setTimesheets(nextTimesheets);
        setObjectives(objectiveData);
        writeHomeCache(cacheKey, { timesheets: nextTimesheets, objectives: objectiveData });
      } catch (err) {
        console.error('Failed to fetch operational summaries:', err);
      }
    };

    const cancelIdle = runWhenIdle(fetchOperationalSummaries, cached ? 1800 : 900);
    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [user, homeCachePrefix]);

  const expenseSummary = useMemo(() => ({
    total: expenses.length,
    submitted: expenses.filter(e => e.status === ExpenseStatus.SUBMITTED).length,
    managerApproved: expenses.filter(e => e.status === ExpenseStatus.MANAGER_APPROVED).length,
    financeApproved: expenses.filter(e => e.status === ExpenseStatus.FINANCE_APPROVED).length,
    reimbursed: expenses.filter(e => e.status === ExpenseStatus.REIMBURSED).length,
    rejected: expenses.filter(e => e.status === ExpenseStatus.MANAGER_REJECTED || e.status === ExpenseStatus.FINANCE_REJECTED).length,
    draft: expenses.filter(e => e.status === ExpenseStatus.DRAFT).length,
  }), [expenses]);

  const nominationSummary = useMemo(() => ({
    total: nominations.length,
    submitted: nominations.filter(n => n.status === NominationStatus.SUBMITTED).length,
    underReview: nominations.filter(n => n.status === NominationStatus.UNDER_REVIEW).length,
    approved: nominations.filter(n => n.status === NominationStatus.APPROVED).length,
    published: nominations.filter(n => n.status === NominationStatus.PUBLISHED).length,
    rejected: nominations.filter(n => n.status === NominationStatus.REJECTED).length,
    draft: nominations.filter(n => n.status === NominationStatus.DRAFT).length,
  }), [nominations]);

  const timesheetSummary = useMemo(() => ({
    total: timesheets.length,
    submitted: timesheets.filter(t => t.status === TimesheetStatus.SUBMITTED).length,
    approved: timesheets.filter(t => t.status === TimesheetStatus.APPROVED).length,
    returned: timesheets.filter(t => t.status === TimesheetStatus.RETURNED).length,
  }), [timesheets]);

  const objectiveSummary = useMemo(() => ({
    total: objectives.length,
    submitted: objectives.filter(o => o.status === ObjectiveStatus.SUBMITTED).length,
    approved: objectives.filter(o => o.status === ObjectiveStatus.APPROVED).length,
    completed: objectives.filter(o => o.status === ObjectiveStatus.COMPLETED).length,
  }), [objectives]);

  // Only pages this role may open are offered, so a stored favourite cannot outlive
  // a role change into a dead shortcut.
  const favoritablePages = useMemo(
    () => getFavoritablePages(user, nominationRole),
    [user, nominationRole]
  );

  const pinnedPages = useMemo(
    () => resolveFavorites(favorites, favoritablePages),
    [favorites, favoritablePages]
  );

  const favoriteGroups = useMemo(() => groupPages(favoritablePages), [favoritablePages]);

  // Recomputed per render rather than memoised on a date: the tab can sit open past
  // midnight, and "upcoming" must not go stale.
  const upcomingHolidays = getUpcomingHolidays(2);

  const formatHoliday = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      day: `${day}`,
      month: date.toLocaleDateString('en-IN', { month: 'short' }),
      full: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', weekday: 'short' }),
    };
  };

  const formatCountdown = (days: number) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `in ${days} days`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getRoleBadge = () => {
    if (!user) return '';
    switch (user.role) {
      case UserRole.SUPER_ADMIN: return 'Super Admin';
      case UserRole.FINANCE_ADMIN: return 'Finance Admin';
      case UserRole.HR_ADMIN: return 'HR Admin';
      case UserRole.MANAGER: return 'Manager';
      default: return 'Employee';
    }
  };

  const getRoleGradient = () => {
    if (!user) return 'linear-gradient(135deg, #0d8a6a, #14b8a6)';
    switch (user.role) {
      case UserRole.SUPER_ADMIN: return 'linear-gradient(135deg, #d97706, #f59e0b)';
      case UserRole.FINANCE_ADMIN: return 'linear-gradient(135deg, #7c3aed, #a78bfa)';
      case UserRole.HR_ADMIN: return 'linear-gradient(135deg, #0d8a6a, #14b8a6)';
      case UserRole.MANAGER: return 'linear-gradient(135deg, #2563eb, #60a5fa)';
      default: return 'linear-gradient(135deg, #0d8a6a, #14b8a6)';
    }
  };

  const getDashboardPath = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case UserRole.SUPER_ADMIN: return '/super-admin';
      case UserRole.FINANCE_ADMIN: return '/admin';
      case UserRole.HR_ADMIN: return '/admin';
      case UserRole.MANAGER: return '/manager';
      default: return '/dashboard';
    }
  };

  const recentExpenses = useMemo(() => [...expenses]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4), [expenses]);

  const recentNominations = useMemo(() => [...nominations]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4), [nominations]);

  const getStatusClass = (status: string) => {
    const s = status.toLowerCase().replace(/\s+/g, '-');
    if (s.includes('approved') || s === 'published' || s === 'reimbursed') return 'status-success';
    if (s.includes('rejected')) return 'status-danger';
    if (s === 'submitted' || s === 'under-review') return 'status-warning';
    return 'status-default';
  };

  const getStatusIcon = (status: string) => {
    const s = status.toLowerCase().replace(/\s+/g, '-');
    if (s.includes('approved') || s === 'published' || s === 'reimbursed') return '✓';
    if (s.includes('rejected')) return '✕';
    if (s === 'submitted' || s === 'under-review') return '⏳';
    return '○';
  };

  return (
    <div className="home-page">
      {/* Hero Carousel with Overlay Welcome */}
      <div className="hero-section">
        <div className="carousel-container">
          <div className="carousel-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {/*
              * Only the visible slide and its two neighbours carry an <img>.
              *
              * Seventeen <img> tags in the track meant seventeen photographs downloaded before
              * anybody had seen the second one — measured at 17 MB on a page whose band shows
              * one 520px picture at a time, and it was the single largest cost of opening the
              * app. The slide divs stay so the translateX track keeps its geometry; only the
              * pictures wait.
              *
              * Neighbours rather than the current one alone, so an auto-advance or a click has
              * the next image already decoded and does not flash an empty band. `loading` is
              * belt and braces on top of that, and the first slide is the one the page is
              * judged on — it loads eagerly, at high priority.
              */}
            {carouselImages.map((img, index) => {
              const distance = Math.min(
                Math.abs(index - currentSlide),
                carouselImages.length - Math.abs(index - currentSlide)
              );
              return (
                <div className="carousel-slide" key={index}>
                  {distance <= 1 && (
                    <img
                      src={img.src}
                      alt={img.caption}
                      className="carousel-image"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={index === currentSlide ? 'high' : 'low'}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="hero-overlay" />
          <div className="hero-content">
            <div className="hero-welcome">
              <div className="hero-greeting-row">
                <img src="/ci-logo.png" alt="Creyente InfoTech" className="hero-logo" />
                <div>
                  <h1 className="hero-greeting">{getGreeting()}, {user?.name?.split(' ')[0] || 'there'}</h1>
                  <div className="hero-meta">
                    <span className="hero-role-badge" style={{ background: getRoleGradient() }}>{getRoleBadge()}</span>
                    {user?.department && <span className="hero-dept">{user.department}</span>}
                  </div>
                </div>
              </div>
              <div className="hero-quote">
                <span className="hero-quote-mark">"</span>
                <p className="hero-quote-text">{todayQuote.text}</p>
                <span className="hero-quote-author">— {todayQuote.author}</span>
              </div>
            </div>
            {/* Indexed directly, so it needs the list to be non-empty AND the index to be in it.
                With nothing configured this renders no caption rather than throwing on
                `undefined.caption`, which is what the hardcoded array made impossible. */}
            {carouselImages[currentSlide] && (
              <div className="carousel-caption-overlay">{carouselImages[currentSlide].caption}</div>
            )}
          </div>
          {/* Arrows and dots only where there is something to move between. One image needs no
              controls, and none at all would leave buttons that do nothing but restart a timer. */}
          {slideCount > 1 && (
            <>
              <button className="carousel-btn carousel-btn-prev" onClick={prevSlide} aria-label="Previous slide">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button className="carousel-btn carousel-btn-next" onClick={nextSlide} aria-label="Next slide">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </button>
              <div className="carousel-dots">
                {carouselImages.map((_, index) => (
                  <button key={index} className={`carousel-dot ${index === currentSlide ? 'active' : ''}`} onClick={() => goToSlide(index)} aria-label={`Go to slide ${index + 1}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quick Actions Strip */}
      <div className="quick-actions-strip">
        <div className="quick-action-card" onClick={() => navigate(getDashboardPath())}>
          <div className="qa-icon qa-icon-blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </div>
          <span className="qa-label">Dashboard</span>
          <span className="qa-arrow">→</span>
        </div>
        <div className="quick-action-card" onClick={() => navigate('/expenses/new')}>
          <div className="qa-icon qa-icon-green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
          </div>
          <span className="qa-label">New Expense</span>
          <span className="qa-arrow">→</span>
        </div>
        {canCreateNomination(nominationRole) && (
          <div className="quick-action-card" onClick={() => navigate('/nominations/new')}>
            <div className="qa-icon qa-icon-amber">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
            </div>
            <span className="qa-label">Nominate</span>
            <span className="qa-arrow">→</span>
          </div>
        )}
        <div className="quick-action-card" onClick={() => navigate('/nominations/feed')}>
          <div className="qa-icon qa-icon-purple">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          </div>
          <span className="qa-label">Recognition Feed</span>
          <span className="qa-arrow">→</span>
        </div>
      </div>

      {/*
        * Attendance summary.
        *
        * Above the widget row rather than inside it: today's IN/OUT is the one thing on this
        * page that changes hour to hour, and it reads as a status line rather than as a panel
        * alongside Favorites. It loads on its own and stays silent on failure — see the
        * component — so it can never put an error on everybody's dashboard.
        */}
      {/*
        * Wrapped in a section that carries the dashboard's gutters.
        *
        * The panel used to sit directly under .home-page with no container, so it ran the full
        * width while the quick-action strip above it and the widgets below both inset 24px — the
        * one band on the page whose edges lined up with nothing.
        */}
      <section className="home-attendance">
        <div className="widget-panel">
          {/* No fallback: the card renders its own skeleton once loaded, and a second placeholder
              underneath it would flash a different shape first. */}
          <React.Suspense fallback={null}>
            <AttendanceSummaryWidget />
          </React.Suspense>
        </div>
      </section>

      {/* Favorites + Upcoming Holidays */}
      <div className="home-widgets">
        <div className="widget-panel favorites-panel">
          <div className="widget-header">
            <div className="widget-title-row">
              <span className="widget-icon widget-icon-amber">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
              </span>
              <h2 className="widget-title">My Favorites</h2>
              {pinnedPages.length > 0 && (
                <span className="favorites-count">{pinnedPages.length}/{MAX_FAVORITES}</span>
              )}
            </div>
            <button
              className="widget-action"
              onClick={() => setEditingFavorites(prev => !prev)}
              aria-expanded={editingFavorites}
            >
              {editingFavorites ? 'Done' : pinnedPages.length > 0 ? 'Customize' : 'Add'}
            </button>
          </div>

          {pinnedPages.length > 0 ? (
            <div className="favorites-grid">
              {pinnedPages.map(page => (
                <div className="favorite-chip" key={page.path}>
                  <button className="favorite-chip-main" onClick={() => navigate(page.path)}>
                    {/* Icon keyed on the page's group. Every other navigable thing in the
                        app leads with one — the sidebar, quick actions, the settings
                        cards — so a text-only chip read as unfinished next to them. */}
                    <span className="favorite-chip-icon" aria-hidden="true">
                      {FAVORITE_GROUP_ICONS[page.group] || '📄'}
                    </span>
                    <span className="favorite-chip-text">
                      <span className="favorite-chip-label">{page.label}</span>
                      <span className="favorite-chip-group">{page.group}</span>
                    </span>
                  </button>
                  <button
                    className="favorite-chip-remove"
                    onClick={() => removeFavorite(page.path)}
                    aria-label={`Remove ${page.label} from favorites`}
                    title={`Remove ${page.label}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </div>
              ))}
            </div>
          ) : !editingFavorites && (
            <div className="favorites-empty">
              <p>No favorites pinned yet.</p>
              <button className="favorites-empty-btn" onClick={() => setEditingFavorites(true)}>
                Pick the pages you use most
              </button>
            </div>
          )}

          {editingFavorites && (
            <div className="favorites-picker">
              <div className="favorites-picker-hint">
                {isFull
                  ? `Maximum ${MAX_FAVORITES} favorites reached — remove one to add another.`
                  : `Select up to ${MAX_FAVORITES} pages.`}
              </div>
              {favoriteGroups.map(group => (
                <div className="favorites-picker-group" key={group.group}>
                  <div className="favorites-picker-group-title">{group.group}</div>
                  <div className="favorites-picker-options">
                    {group.pages.map(page => {
                      const pinned = isFavorite(page.path);
                      return (
                        <button
                          key={page.path}
                          className={`favorites-picker-option ${pinned ? 'selected' : ''}`}
                          onClick={() => toggleFavorite(page.path)}
                          disabled={!pinned && isFull}
                          aria-pressed={pinned}
                        >
                          <span className="favorites-picker-star">{pinned ? '★' : '☆'}</span>
                          {page.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="widget-panel holidays-panel">
          <div className="widget-header">
            <div className="widget-title-row">
              <span className="widget-icon widget-icon-teal">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              </span>
              <h2 className="widget-title">Upcoming Holidays</h2>
            </div>
          </div>

          {upcomingHolidays.length > 0 ? (
            <div className="holidays-list">
              {upcomingHolidays.map(holiday => {
                const days = daysUntilHoliday(holiday);
                const parts = formatHoliday(holiday.date);
                return (
                  <div className="holiday-item" key={holiday.date}>
                    <div className="holiday-date-badge">
                      <span className="holiday-day">{parts.day}</span>
                      <span className="holiday-month">{parts.month}</span>
                    </div>
                    <div className="holiday-content">
                      <span className="holiday-name">{holiday.name}</span>
                      <span className="holiday-meta">{parts.full}</span>
                    </div>
                    <span className={`holiday-countdown ${days <= 1 ? 'imminent' : ''}`}>
                      {formatCountdown(days)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="holidays-empty">
              <p>No upcoming holidays in the published calendar.</p>
            </div>
          )}
        </div>
      </div>

      {/* Animated Stats Overview */}
      <div className="stats-overview" ref={statsRef}>
        <div className={`stat-card stat-card-blue ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '0ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{expenseSummary.total}</div>
          <div className="stat-card-label">Total Expenses</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
          </div>
        </div>
        <div className={`stat-card stat-card-amber ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '100ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{expenseSummary.submitted}</div>
          <div className="stat-card-label">Pending Review</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          </div>
        </div>
        <div className={`stat-card stat-card-green ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '200ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{expenseSummary.reimbursed}</div>
          <div className="stat-card-label">Reimbursed</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          </div>
        </div>
        <div className={`stat-card stat-card-purple ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '300ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{nominationSummary.published}</div>
          <div className="stat-card-label">Recognitions</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
          </div>
        </div>
        <div className={`stat-card stat-card-cyan ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '400ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{timesheetSummary.total}</div>
          <div className="stat-card-label">Timesheets</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 15 14" /></svg>
          </div>
        </div>
        <div className={`stat-card stat-card-rose ${animatedStats ? 'animate-in' : ''}`} style={{ animationDelay: '500ms' }}>
          <div className="stat-card-glow" />
          <div className="stat-card-number">{objectiveSummary.total}</div>
          <div className="stat-card-label">Performance Goals</div>
          <div className="stat-card-icon-bg">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></svg>
          </div>
        </div>
      </div>

      {/* Activity Timelines - Side by Side */}
      <div className="activity-section">
        {/* Expense Activity */}
        <div className="activity-panel">
          <div className="activity-panel-header">
            <div className="activity-panel-title-row">
              <div className="activity-panel-dot expense-dot" />
              <h2 className="activity-panel-title">Expense Activity</h2>
            </div>
            <button className="activity-view-all" onClick={() => navigate(getDashboardPath())}>View All →</button>
          </div>
          {loadingExpenses ? (
            <div className="activity-loading">
              <div className="loading-pulse" /><div className="loading-pulse" /><div className="loading-pulse" />
            </div>
          ) : recentExpenses.length > 0 ? (
            <div className="activity-timeline">
              {recentExpenses.map((exp, idx) => (
                <div className="timeline-item" key={exp.id} onClick={() => navigate(`/expenses/${exp.id}`)} style={{ animationDelay: `${idx * 80}ms` }}>
                  <div className="timeline-line">
                    <div className={`timeline-dot-icon ${getStatusClass(exp.status)}`}>{getStatusIcon(exp.status)}</div>
                    {idx < recentExpenses.length - 1 && <div className="timeline-connector" />}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-content-top">
                      <span className="timeline-title">{exp.title}</span>
                      <span className={`timeline-status ${getStatusClass(exp.status)}`}>{exp.status.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="timeline-meta">
                      <span className="timeline-amount">₹{exp.amount.toLocaleString()}</span>
                      <span className="timeline-date">{new Date(exp.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="activity-empty">
              <div className="activity-empty-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
              </div>
              <p>No expense claims yet</p>
              <button className="activity-empty-btn" onClick={() => navigate('/expenses/new')}>Submit your first claim</button>
            </div>
          )}
        </div>

        {/* Nomination Activity */}
        <div className="activity-panel">
          <div className="activity-panel-header">
            <div className="activity-panel-title-row">
              <div className="activity-panel-dot nomination-dot" />
              <h2 className="activity-panel-title">
                {canManageAllNominations(nominationRole) || canReviewNomination(nominationRole)
                  ? 'Nomination Overview'
                  : canCreateNomination(nominationRole)
                    ? 'My Nominations'
                    : 'Recent Recognitions'}
              </h2>
            </div>
            <button className="activity-view-all" onClick={() => navigate(
              canManageAllNominations(nominationRole) ? '/nominations/admin'
                : canReviewNomination(nominationRole) ? '/nominations/review'
                  : canCreateNomination(nominationRole) ? '/nominations/my'
                    : '/nominations/feed'
            )}>View All →</button>
          </div>
          {loadingNominations ? (
            <div className="activity-loading">
              <div className="loading-pulse" /><div className="loading-pulse" /><div className="loading-pulse" />
            </div>
          ) : recentNominations.length > 0 ? (
            <div className="activity-timeline">
              {recentNominations.map((nom, idx) => (
                <div className="timeline-item" key={nom.id} onClick={() => { if (canCreateNomination(nominationRole)) navigate(`/nominations/${nom.id}`); }} style={{ cursor: canCreateNomination(nominationRole) ? 'pointer' : 'default', animationDelay: `${idx * 80}ms` }}>
                  <div className="timeline-line">
                    <div className={`timeline-dot-icon ${getStatusClass(nom.status)}`}>{getStatusIcon(nom.status)}</div>
                    {idx < recentNominations.length - 1 && <div className="timeline-connector" />}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-content-top">
                      <span className="timeline-title">{nom.nomineeName}</span>
                      <span className={`timeline-status ${getStatusClass(nom.status)}`}>{nom.status.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="timeline-meta">
                      <span className="timeline-award">{nom.awardType}</span>
                      <span className="timeline-date">{new Date(nom.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="activity-empty">
              <div className="activity-empty-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
              </div>
              <p>{canCreateNomination(nominationRole) ? 'No nominations yet' : 'No published recognitions yet'}</p>
              {canCreateNomination(nominationRole) && (
                <button className="activity-empty-btn" onClick={() => navigate('/nominations/new')}>Nominate someone</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nomination Stats Bar (for managers/admins) */}
      {(canManageAllNominations(nominationRole) || canReviewNomination(nominationRole) || canCreateNomination(nominationRole)) && !loadingNominations && nominationSummary.total > 0 && (
        <div className="nomination-stats-bar">
          <div className="nsb-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
            Nomination Pipeline
          </div>
          <div className="nsb-items">
            {nominationSummary.draft > 0 && <div className="nsb-item nsb-draft"><span className="nsb-count">{nominationSummary.draft}</span> Draft</div>}
            {nominationSummary.submitted > 0 && <div className="nsb-item nsb-submitted"><span className="nsb-count">{nominationSummary.submitted}</span> Submitted</div>}
            {nominationSummary.underReview > 0 && <div className="nsb-item nsb-review"><span className="nsb-count">{nominationSummary.underReview}</span> In Review</div>}
            {nominationSummary.approved > 0 && <div className="nsb-item nsb-approved"><span className="nsb-count">{nominationSummary.approved}</span> Approved</div>}
            {nominationSummary.published > 0 && <div className="nsb-item nsb-published"><span className="nsb-count">{nominationSummary.published}</span> Published</div>}
            {nominationSummary.rejected > 0 && <div className="nsb-item nsb-rejected"><span className="nsb-count">{nominationSummary.rejected}</span> Rejected</div>}
          </div>
        </div>
      )}

      <footer className="home-footer">
        <p>© 2026 Creyente InfoTech Pvt. Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default HomePage;
